import React, { useState, useEffect, memo } from 'react'
import { 
  FileText,
  Plus,
  Edit,
  Trash2,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  RotateCcw
} from 'lucide-react'
import { useContractModal } from '../../contexts/ContractModalContext'
import ModalInativarContrato from './ModalInativarContrato'

interface Contrato {
  id: string
  numero_contrato: string
  tipo_contrato: 'principal' | 'adicional' | 'renovacao' | 'upgrade' | 'projeto' | 'aditivo'
  status_relacionamento: 'independente' | 'vinculado' | 'substitui' | 'complementa'
  plano_ids: string[]
  conteudo_original: string
  conteudo_editado: string
  dados_variaveis: any
  data_inicio?: string
  data_fim?: string
  status: 'ativo' | 'inativo' | string
  data_encerramento?: string
  motivo_encerramento?: string
  observacoes?: string
  arquivo_url?: string
  created_at: string
  updated_at: string
  contrato_pai?: {
    numero_contrato: string
    tipo_contrato: string
  }
  contrato_substitui?: {
    numero_contrato: string
    tipo_contrato: string
  }
}

interface Plano {
  id: string
  nome: string
  valor_base: number
  status: string
}

interface ClienteContratosProps {
  clienteId: string
}

const ClienteContratos: React.FC<ClienteContratosProps> = ({ clienteId }) => {
  const [contratos, setContratos] = useState<Contrato[]>([])
  const [planos, setPlanos] = useState<Plano[]>([])
  const [loading, setLoading] = useState(true)
  const [showInativarModal, setShowInativarModal] = useState(false)
  const [contratoParaInativar, setContratoParaInativar] = useState<Contrato | null>(null)

  // Use context for modal and editor state
  const { 
    openModal, 
    setCallbacks,
    openEditor
  } = useContractModal()

  useEffect(() => {
    fetchContratos()
    fetchPlanos()
  }, [])

  // Set up callbacks for the context
  useEffect(() => {
    setCallbacks({
      onContractCreated: handleContractCreated,
      onEditContract: handleEditContract
    })
  }, [setCallbacks])

  const fetchContratos = async () => {
    try {
      const response = await fetch(`/api/contratos?cliente_id=${clienteId}`)
      const data = await response.json()
      
      if (data.data) {
        setContratos(data.data)
      }
    } catch (error) {
      console.error('Erro ao carregar contratos:', error)
    }
  }

  const fetchPlanos = async () => {
    try {
      const response = await fetch('/api/planos')
      const data = await response.json()
      
      if (data.data) {
        const planosCliente = data.data.filter((plano: any) => plano.cliente_id === clienteId)
        setPlanos(planosCliente)
      }
    } catch (error) {
      console.error('Erro ao carregar planos:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleContractCreated = (contrato: Contrato) => {
    console.log('🎉 Contract created, updating list:', contrato.numero_contrato)
    setContratos(prev => [contrato, ...prev])
  }

  const handleEditContract = (contrato: Contrato) => {
    console.log('✏️ Opening editor for contract:', contrato.numero_contrato)
    openEditor(contrato)
  }

  const handleOpenModal = () => {
    console.log('🚀 Opening modal for client:', clienteId)
    openModal(clienteId, planos)
  }

  const handleOpenInativarModal = (contrato: Contrato) => {
    setContratoParaInativar(contrato)
    setShowInativarModal(true)
  }

  const handleConfirmInativar = async (contratoId: string, dataEncerramento: string, motivoEncerramento: string) => {
    try {
      const contrato = contratos.find(c => c.id === contratoId)
      const isInativar = contrato?.status === 'ativo'

      const response = await fetch(`/api/contratos?id=${contratoId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: isInativar ? 'inativo' : 'ativo',
          data_encerramento: isInativar ? dataEncerramento : null,
          motivo_encerramento: isInativar ? motivoEncerramento : null
        })
      })

      if (response.ok) {
        await fetchContratos()
        alert(isInativar ? '✅ Contrato inativado com sucesso!' : '✅ Contrato reativado com sucesso!')
      } else {
        const result = await response.json()
        alert(`Erro: ${result.error || 'Erro ao atualizar contrato'}`)
      }
    } catch (error) {
      console.error('Erro ao atualizar status do contrato:', error)
      alert('Erro ao atualizar status do contrato')
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR')
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ativo':
      case 'finalizado':
        return <CheckCircle className="w-4 h-4 text-green-600" />
      case 'inativo':
        return <XCircle className="w-4 h-4 text-red-600" />
      case 'rascunho':
        return <Clock className="w-4 h-4 text-yellow-600" />
      default:
        return <AlertCircle className="w-4 h-4 text-gray-600" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ativo':
      case 'finalizado':
        return 'bg-green-100 text-green-800'
      case 'inativo':
        return 'bg-red-100 text-red-800'
      case 'rascunho':
        return 'bg-yellow-100 text-yellow-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getTipoContratoLabel = (tipo: string) => {
    const labels = {
      'principal': 'Principal',
      'adicional': 'Adicional',
      'renovacao': 'Renovação',
      'upgrade': 'Upgrade',
      'projeto': 'Projeto',
      'aditivo': 'Aditivo'
    }
    return labels[tipo as keyof typeof labels] || tipo
  }

  const handleExcluirContrato = async (contratoId: string) => {
    if (!confirm('Tem certeza que deseja excluir este contrato?')) {
      return
    }

    try {
      const response = await fetch(`/api/contratos?id=${contratoId}`, {
        method: 'DELETE'
      })

      if (response.ok) {
        await fetchContratos()
      } else {
        const result = await response.json()
        alert('Erro ao excluir contrato: ' + result.error)
      }
    } catch (error) {
      console.error('Erro ao excluir contrato:', error)
      alert('Erro ao excluir contrato')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Contratos</h3>
        <button
          onClick={handleOpenModal}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <Plus className="w-4 h-4 mr-2" />
          Novo Contrato
        </button>
      </div>

      {/* Modal and Editor now rendered globally */}

      {/* Lista de Contratos */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {contratos.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum contrato encontrado</h3>
            <p className="mt-1 text-sm text-gray-500">
              Comece gerando um novo contrato para este cliente.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {contratos.map((contrato) => (
              <div key={contrato.id} className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <FileText className="w-5 h-5 text-gray-400" />
                    <div>
                      <div className="flex items-center space-x-2 flex-wrap">
                        <h4 className="text-sm font-medium text-gray-900">
                          {contrato.numero_contrato}
                        </h4>
                        <span className="text-xs text-gray-500">
                          {getTipoContratoLabel(contrato.tipo_contrato)}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(contrato.status)}`}>
                          {getStatusIcon(contrato.status)}
                          <span className="ml-1 capitalize">{contrato.status}</span>
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">
                        Criado em {formatDate(contrato.created_at)}
                      </p>
                      {contrato.contrato_pai && (
                        <p className="text-xs text-blue-600">
                          Relacionado: {contrato.contrato_pai.numero_contrato}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => handleEditContract(contrato)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full"
                      title="Editar"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    {(contrato.status === 'ativo' || contrato.status === 'inativo') && (
                      <button
                        type="button"
                        onClick={() => handleOpenInativarModal(contrato)}
                        className={`p-2 rounded-full ${
                          contrato.status === 'ativo'
                            ? 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                            : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
                        }`}
                        title={contrato.status === 'ativo' ? 'Inativar Contrato' : 'Reativar Contrato'}
                      >
                        {contrato.status === 'ativo' ? (
                          <XCircle className="w-4 h-4" />
                        ) : (
                          <RotateCcw className="w-4 h-4" />
                        )}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleExcluirContrato(contrato.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full"
                      title="Excluir"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                {contrato.observacoes && (
                  <div className="mt-3 text-sm text-gray-600">
                    <strong>Observações:</strong> {contrato.observacoes}
                  </div>
                )}

                {/* Mostrar informações de encerramento se inativo */}
                {contrato.status === 'inativo' && (contrato.data_encerramento || contrato.motivo_encerramento) && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="text-sm">
                      {contrato.data_encerramento && (
                        <p className="text-red-800">
                          <strong>Data de Encerramento:</strong> {formatDate(contrato.data_encerramento)}
                        </p>
                      )}
                      {contrato.motivo_encerramento && (
                        <p className="text-red-700 mt-1">
                          <strong>Motivo:</strong> {contrato.motivo_encerramento}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de Inativação/Reativação */}
      {showInativarModal && contratoParaInativar && (
        <ModalInativarContrato
          contrato={contratoParaInativar}
          isOpen={showInativarModal}
          onClose={() => {
            setShowInativarModal(false)
            setContratoParaInativar(null)
          }}
          onConfirm={handleConfirmInativar}
        />
      )}
    </div>
  )
}

export default memo(ClienteContratos)
